import { reportClientError } from "@pax/telemetry/client";
import { buttonVariants, HapticSurface } from "@pax/ui";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { type ErrorComponentProps, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { ApiProblem } from "@/components/ApiProblem";
import { NoAccess } from "@/features/auth/permissions";
import { isNotFound } from "@/lib/api";

export function RouteError({ error }: ErrorComponentProps) {
  const { reset } = useQueryErrorResetBoundary();

  useEffect(() => {
    reset();
    if (!isNotFound(error) && !(error instanceof NoAccess)) reportClientError(error);
  }, [reset, error]);

  if (isNotFound(error)) return <NotFound />;
  if (error instanceof NoAccess) return <NoAccessScreen />;

  const showsMessage =
    error instanceof Error && (import.meta.env.DEV || !(error instanceof TypeError));
  const message = showsMessage ? error.message.trim() : "";
  return <ApiProblem message={message || "Try again in a moment."} />;
}

export function NotFound() {
  return (
    <main className="mx-auto flex max-w-xl flex-col items-center px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">Not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">There is nothing at this address.</p>
      <Link to="/" className={buttonVariants({ variant: "outline", className: "mt-6" })}>
        Browse scenarios
        <HapticSurface />
      </Link>
    </main>
  );
}

/** A staff page the reader holds no role for. A new role takes up to five minutes to show. */
function NoAccessScreen() {
  return (
    <main className="mx-auto flex max-w-xl flex-col items-center px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">You don't have access to this page</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        It needs a staff role you don't hold. Someone who manages staff can assign one, and it can
        take a few minutes to show.
      </p>
      <Link to="/" className={buttonVariants({ variant: "outline", className: "mt-6" })}>
        Browse scenarios
        <HapticSurface />
      </Link>
    </main>
  );
}
